import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ProviderBadges } from "@/components"
import { EmptyDataPanel } from "@/components/System"
import {
  useGetConnectionsApi,
  connectionResponseToConnection,
  useGetOrganizationUsersApi,
} from "@/hooks/api"
import { Connection, ConnectionStatus } from "@/types"
import { useStore } from "@/store"
import { useToast } from "@/hooks"
import ConnectionListItem from "./ConnectionListItem"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

/**
 * ConnectionsTab - Displays the list of connections within the Knowledge Base page.
 * This reuses the same functionality from the original ConnectionList page.
 */
const ConnectionsTab = () => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const timerIdRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const [connections, setConnections] = useState<Connection[]>([])
  const [displayConnections, setDisplayConnections] = useState<Connection[]>([])
  const [providerFilter, setProviderFilter] = useState<string>("all")

  const [currentOrganization] = useStore((state) => [state.currentOrganization])

  // APIs
  const {
    error: connectionsError,
    data: connectionsResp,
    fetchFn: getConnections,
  } = useGetConnectionsApi()

  // Fetch organization users for owner name lookup
  const {
    error: orgUsersError,
    data: orgUsersResp,
    fetchFn: getOrgUsers,
  } = useGetOrganizationUsersApi(currentOrganization?.id || "")

  // Error handling for connections API
  useEffect(() => {
    if (!connectionsError) return
    console.error("Get connections API error:", connectionsError)
    alertToast({
      title: t("constants.errors.somethingWrong"),
      description: getUserFacingApiErrorMessage(
        connectionsError,
        t,
        (connectionsResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [connectionsError, connectionsResp, alertToast, t])

  // Error handling for organization users API
  useEffect(() => {
    if (!orgUsersError) return
    console.error("Get organization users API error:", orgUsersError)
    alertToast({
      title: t("constants.errors.somethingWrong"),
      description: getUserFacingApiErrorMessage(
        orgUsersError,
        t,
        (orgUsersResp as { message?: string } | undefined)?.message ||
          t("constants.errors.somethingWrong")
      ),
    })
  }, [orgUsersError, orgUsersResp, alertToast, t])

  useEffect(() => {
    if (currentOrganization?.id) {
      getOrgUsers({
        searchParams: new URLSearchParams({
          limit: "9999",
          offset: "0",
          enabled: "true",
        }),
      })
    }
  }, [currentOrganization?.id, getOrgUsers])

  // Build ownerId -> ownerName map
  const ownerNameMap = useMemo(() => {
    const map = new Map<string, string>()
    if (orgUsersResp && orgUsersResp.data) {
      for (const user of orgUsersResp.data) {
        map.set(
          user.id.toString(),
          user.name || user.email || `User ${user.id}`
        )
      }
    }
    return map
  }, [orgUsersResp])

  const refreshConnections = useCallback(() => {
    getConnections({
      searchParams: new URLSearchParams({ limit: "9999" }),
    })
  }, [getConnections])

  useEffect(() => {
    refreshConnections()
    return () => {
      if (!timerIdRef.current) return
      clearTimeout(timerIdRef.current)
    }
  }, [refreshConnections])

  useEffect(() => {
    if (!connectionsResp || !connectionsResp.data) {
      setConnections([])
      return
    }

    let isProgressing = false
    const result = connectionsResp.data.map((connection) => {
      const conn = connectionResponseToConnection(connection)
      if (
        conn.status === ConnectionStatus.InProgress ||
        conn.status === ConnectionStatus.ScopesInitializing
      ) {
        isProgressing = true
      }
      return conn
    })
    setConnections(result)

    if (isProgressing) {
      if (timerIdRef.current) clearTimeout(timerIdRef.current)
      timerIdRef.current = setTimeout(refreshConnections, 5000)
    }

    const filteredConnections = result.filter((connection) => {
      if (providerFilter !== "all" && connection.provider !== providerFilter) {
        return false
      }
      return true
    })
    setDisplayConnections(filteredConnections)
  }, [connectionsResp, providerFilter, refreshConnections])

  const handleDeleteConnection = () => {
    refreshConnections()
  }

  const handleProviderFilterClick = (provider: string) => {
    setProviderFilter(provider)
  }

  return (
    <>
      <div className="mb-4">
        <ProviderBadges
          providers={connections.map((connection) => connection.provider)}
          onClick={handleProviderFilterClick}
        />
      </div>

      {displayConnections.length === 0 ? (
        <div className="flex justify-center text-gray-500 text-center my-12">
          <EmptyDataPanel />
        </div>
      ) : (
        <div className="mb-10 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-800">
          {displayConnections.map((connection: Connection) => {
            const ownerName = connection.ownerId
              ? ownerNameMap.get(connection.ownerId) ||
                `User ${connection.ownerId}`
              : undefined
            return (
              <ConnectionListItem
                key={connection.id}
                connection={connection}
                ownerName={ownerName}
                onDelete={handleDeleteConnection}
              />
            )
          })}
        </div>
      )}
    </>
  )
}

export default ConnectionsTab
